/**
 * CADModelViewer - Three.js 3D model viewer for GLB files
 *
 * Displays a CAD model using React Three Fiber with orbit controls,
 * grid, wireframe toggle, and auto-fit camera.
 */

import React, { Suspense, useState, useEffect, useRef, Component, ErrorInfo, ReactNode } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls, Grid, useGLTF, Center, Html } from '@react-three/drei';
import { Box, IconButton, Tooltip, Stack, Typography, CircularProgress, Button } from '@mui/material';
import {
  GridOn as GridIcon,
  GridOff as GridOffIcon,
  ViewInAr as WireframeIcon,
  CenterFocusStrong as FitIcon,
  Refresh as ResetIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import * as THREE from 'three';
import { CADMarker } from '../../types/cad';

/**
 * Error boundary to catch Three.js / GLB loading errors
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  onReset?: () => void;
  loadFailedLabel: string;
  retryLabel: string;
  unknownErrorLabel: string;
}
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ViewerErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('CADModelViewer error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#16213e',
            borderRadius: 1,
            gap: 2,
            p: 3,
          }}
        >
          <ErrorIcon sx={{ fontSize: 48, color: 'error.main' }} />
          <Typography color="error.light" variant="body2" textAlign="center">
            {this.props.loadFailedLabel}
          </Typography>
          <Typography color="grey.500" variant="caption" textAlign="center">
            {this.state.error?.message || this.props.unknownErrorLabel}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            sx={{ color: 'grey.400', borderColor: 'grey.600' }}
            onClick={() => {
              this.setState({ hasError: false, error: null });
              this.props.onReset?.();
            }}
          >
            {this.props.retryLabel}
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

interface CADModelViewerProps {
  glbUrl: string | null;
  filename?: string;
  markers?: CADMarker[];
  /**
   * Fired when a marker sphere is clicked. Used by DRBFM↔CAD linker (TG #673)
   * to open the CADMarkerDetailPanel side drawer.
   */
  onMarkerClick?: (marker: CADMarker, index: number) => void;
}

const MARKER_COLORS: Record<CADMarker['type'], string> = {
  info: '#2196f3',
  warning: '#ff9800',
  error: '#f44336',
};

/**
 * Renders 3D sphere markers with HTML labels inside the Canvas.
 * Must be placed inside <Center> to share the model's coordinate space.
 */
function MarkerOverlay({
  markers,
  modelRadius,
  onMarkerClick,
}: {
  markers: CADMarker[];
  modelRadius: number;
  onMarkerClick?: (marker: CADMarker, index: number) => void;
}) {
  const radius = Math.max(modelRadius * 0.04, 0.8);

  return (
    <>
      {markers.map((m, i) => (
        <group key={`marker-${m.position.join(',')}-${i}`} position={m.position}>
          <mesh
            onPointerDown={(e) => {
              if (!onMarkerClick) return;
              e.stopPropagation();
              onMarkerClick(m, i);
            }}
            onPointerOver={(e) => {
              if (!onMarkerClick) return;
              e.stopPropagation();
              document.body.style.cursor = 'pointer';
            }}
            onPointerOut={(e) => {
              if (!onMarkerClick) return;
              e.stopPropagation();
              document.body.style.cursor = '';
            }}
          >
            <sphereGeometry args={[radius, 16, 16]} />
            <meshStandardMaterial
              color={MARKER_COLORS[m.type]}
              transparent
              opacity={0.7}
              emissive={MARKER_COLORS[m.type]}
              emissiveIntensity={0.8}
            />
          </mesh>
          <Html
            center
            distanceFactor={modelRadius * 0.5}
            style={{ pointerEvents: 'none' }}
            zIndexRange={[1, 0]}
          >
            <div style={{
              background: MARKER_COLORS[m.type],
              color: 'white',
              padding: '4px 10px',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              transform: 'translateY(-20px)',
              boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
            }}>
              {m.label}
            </div>
          </Html>
        </group>
      ))}
    </>
  );
}

/**
 * Inner component that loads and displays the GLB model, then auto-fits camera
 */
function Model({ url, wireframe, onLoaded, onModelRadius }: { url: string; wireframe: boolean; onLoaded: () => void; onModelRadius?: (r: number) => void }) {
  const { scene } = useGLTF(url);
  const { camera } = useThree();
  const fitted = useRef(false);

  const clonedScene = React.useMemo(() => {
    const cloned = scene.clone(true);
    cloned.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.material = child.material.clone();
        child.material.wireframe = wireframe;
        // Blue material for visibility on dark background
        child.material.color = new THREE.Color(0x6aadff);
        child.material.metalness = 0.2;
        child.material.roughness = 0.5;
        child.material.emissive = new THREE.Color(0x111122);
        child.material.emissiveIntensity = 0.1;
      }
    });
    return cloned;
  }, [scene, wireframe]);

  // Auto-fit camera after model is loaded and report model radius
  useEffect(() => {
    if (!fitted.current) {
      fitted.current = true;
      fitCamera(clonedScene, camera);
      onLoaded();
      // Compute bounding sphere radius for marker sizing
      const box = new THREE.Box3().setFromObject(clonedScene);
      const sphere = new THREE.Sphere();
      box.getBoundingSphere(sphere);
      if (sphere.radius > 0) onModelRadius?.(sphere.radius);
    }
  }, [clonedScene, camera, onLoaded, onModelRadius]);

  return <primitive object={clonedScene} />;
}

/**
 * Fit camera to encompass the entire scene
 */
function fitCamera(object: THREE.Object3D, camera: THREE.Camera) {
  const box = new THREE.Box3().setFromObject(object);
  if (box.isEmpty()) return;

  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z);

  if (maxDim === 0) return;

  const perspCam = camera as THREE.PerspectiveCamera;
  const fov = perspCam.fov * (Math.PI / 180);
  const distance = (maxDim / (2 * Math.tan(fov / 2))) * 1.8;

  perspCam.position.set(
    center.x + distance * 0.6,
    center.y + distance * 0.5,
    center.z + distance * 0.6
  );
  perspCam.lookAt(center);
  perspCam.near = distance * 0.01;
  perspCam.far = distance * 10;
  perspCam.updateProjectionMatrix();
}

/**
 * Manual camera fit trigger
 */
function FitCameraToScene({ trigger }: { trigger: number }) {
  const { camera, scene } = useThree();

  useEffect(() => {
    if (trigger > 0) {
      fitCamera(scene, camera);
    }
  }, [trigger, camera, scene]);

  return null;
}

/**
 * Loading fallback inside Canvas
 */
function LoadingFallback() {
  return (
    <mesh rotation={[0, 0, 0]}>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial color="#4a90d9" wireframe transparent opacity={0.5} />
    </mesh>
  );
}

const CADModelViewer: React.FC<CADModelViewerProps> = ({ glbUrl, filename, markers = [], onMarkerClick }) => {
  const { t } = useTranslation();
  const [wireframe, setWireframe] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const [fitTrigger, setFitTrigger] = useState(0);
  const [modelRadius, setModelRadius] = useState<number | null>(null);
  const [centerOffset, setCenterOffset] = useState<[number, number, number]>([0, 0, 0]);
  const [loading, setLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  // Fetch GLB with Authorization header and create blob URL for useGLTF.
  // Three.js useGLTF cannot send custom headers, so we fetch the binary
  // ourselves and hand it a local object URL.
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    const loadGlb = async () => {
      if (!glbUrl) {
        setBlobUrl(null);
        setLoadError(null);
        return;
      }

      setLoading(true);
      setLoadError(null);
      setBlobUrl(null);
      setModelRadius(null);
      setCenterOffset([0, 0, 0]);

      try {
        const token = sessionStorage.getItem('access_token');
        const headers: Record<string, string> = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        const response = await fetch(glbUrl, { headers });
        if (!response.ok) {
          throw new Error(`GLB load failed: ${response.status}`);
        }
        const blob = await response.blob();
        if (!cancelled) {
          objectUrl = URL.createObjectURL(blob);
          setBlobUrl(objectUrl);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : t('cad.viewer.loadFailed'));
          setLoading(false);
        }
      }
    };

    loadGlb();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only builds the load-error message; excluded so a language change doesn't refetch/reload the 3D model (PR #790)
  }, [glbUrl, retryCount]);

  // Revoke previous blob URL when a new one is created, and on unmount
  const prevBlobUrl = useRef<string | null>(null);
  useEffect(() => {
    if (prevBlobUrl.current && prevBlobUrl.current !== blobUrl) {
      useGLTF.clear(prevBlobUrl.current);
      URL.revokeObjectURL(prevBlobUrl.current);
    }
    prevBlobUrl.current = blobUrl;
    return () => {
      // Cleanup on unmount: revoke current blob URL and clear useGLTF cache
      if (prevBlobUrl.current) {
        useGLTF.clear(prevBlobUrl.current);
        URL.revokeObjectURL(prevBlobUrl.current);
        prevBlobUrl.current = null;
      }
    };
  }, [blobUrl]);

  const handleFit = () => setFitTrigger((v) => v + 1);
  const handleReset = () => {
    setWireframe(false);
    setShowGrid(true);
    setFitTrigger((v) => v + 1);
  };

  if (!glbUrl) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          borderRadius: 1,
        }}
      >
        <Typography color="text.secondary">
          {t('cad.viewer.selectModel')}
        </Typography>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#16213e',
          borderRadius: 1,
          gap: 2,
          p: 3,
        }}
      >
        <ErrorIcon sx={{ fontSize: 48, color: 'error.main' }} />
        <Typography color="error.light" variant="body2" textAlign="center">
          {t('cad.viewer.loadFailed')}
        </Typography>
        <Typography color="grey.500" variant="caption" textAlign="center">
          {loadError}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          sx={{ color: 'grey.400', borderColor: 'grey.600' }}
          onClick={() => {
            setLoadError(null);
            setRetryCount((c) => c + 1);
          }}
        >
          {t('cad.viewer.retry')}
        </Button>
      </Box>
    );
  }

  return (
    <ViewerErrorBoundary
      onReset={handleReset}
      loadFailedLabel={t('cad.viewer.loadFailed')}
      retryLabel={t('cad.viewer.retry')}
      unknownErrorLabel={t('common.unknown')}
    >
    <Box sx={{ height: '100%', position: 'relative', bgcolor: '#16213e', borderRadius: 1, overflow: 'hidden' }}>
      {/* Toolbar */}
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 10,
          bgcolor: 'rgba(255,255,255,0.9)',
          borderRadius: 1,
          px: 0.5,
        }}
      >
        <Tooltip title={showGrid ? t('cad.viewer.hideGrid') : t('cad.viewer.showGrid')}>
          <IconButton size="small" onClick={() => setShowGrid(!showGrid)}>
            {showGrid ? <GridIcon fontSize="small" /> : <GridOffIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title={wireframe ? t('cad.viewer.showSolid') : t('cad.viewer.showWireframe')}>
          <IconButton size="small" onClick={() => setWireframe(!wireframe)}>
            <WireframeIcon fontSize="small" color={wireframe ? 'primary' : 'inherit'} />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('cad.viewer.fit')}>
          <IconButton size="small" onClick={handleFit}>
            <FitIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('cad.viewer.reset')}>
          <IconButton size="small" onClick={handleReset}>
            <ResetIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Loading indicator */}
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10,
            textAlign: 'center',
          }}
        >
          <CircularProgress size={32} sx={{ color: 'rgba(255,255,255,0.7)' }} />
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'rgba(255,255,255,0.5)' }}>
            {t('cad.viewer.loading')}
          </Typography>
        </Box>
      )}

      {/* Filename label */}
      {filename && (
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            bottom: 8,
            left: 8,
            zIndex: 10,
            color: 'rgba(255,255,255,0.7)',
            bgcolor: 'rgba(0,0,0,0.5)',
            px: 1,
            py: 0.25,
            borderRadius: 0.5,
          }}
        >
          {filename}
        </Typography>
      )}

      <Canvas camera={{ position: [500, 500, 500], fov: 50, near: 0.1, far: 100000 }}>
        {/* Lighting setup for dark background */}
        <ambientLight intensity={0.6} />
        <directionalLight position={[1000, 1000, 500]} intensity={1.2} />
        <directionalLight position={[-500, 500, -500]} intensity={0.4} />
        <directionalLight position={[0, -500, 0]} intensity={0.2} />
        <hemisphereLight args={['#b1e1ff', '#2a2a3a', 0.5]} />

        <Suspense fallback={<LoadingFallback />}>
          {blobUrl && (
          <Center
            key={blobUrl}
            onCentered={({ center }) => setCenterOffset([-center.x, -center.y, -center.z])}
          >
            <Model
              url={blobUrl}
              wireframe={wireframe}
              onLoaded={() => setLoading(false)}
              onModelRadius={setModelRadius}
            />
          </Center>
          )}
          {markers.length > 0 && modelRadius !== null && (
            <group position={centerOffset}>
              <MarkerOverlay markers={markers} modelRadius={modelRadius} onMarkerClick={onMarkerClick} />
            </group>
          )}
        </Suspense>

        {showGrid && (
          <Grid
            args={[10000, 10000]}
            cellSize={100}
            cellColor="#3a3a5a"
            sectionSize={500}
            sectionColor="#5a5a7a"
            fadeDistance={5000}
            position={[0, -0.5, 0]}
          />
        )}

        <FitCameraToScene trigger={fitTrigger} />
        <OrbitControls makeDefault enableDamping dampingFactor={0.1} />
      </Canvas>
    </Box>
    </ViewerErrorBoundary>
  );
};

export default CADModelViewer;
